import {
  BLUEPRINT_APPLICATION_KEY,
  BLUEPRINT_ENVIRONMENT_KEY,
  PARAMETER_KEY_APPLICATION_ID,
} from "./app-constants.json";

import i18n from "../config/locale/i18n";

import ResourceList from "./resourceList";
import { isArrayWithLength } from "./array-methods";
import { converLabelValueToTerraformFormat } from "../modules/blueprints/helpers/terraformParameters";

export const applicationParameter = {
  description: i18n.t("common:environmentLabelDesc"),
  existingParameter: true,
  initialTemplateType: "String",
  isMultiSelect: false,
  isOptional: false,
  keyField: "resourceId",
  parameterKey: BLUEPRINT_APPLICATION_KEY,
  parameterLabel: i18n.t("common:environmentLabel"),
  parameterType: "String",
  parameterValue: "",
  hideParameter: false,
};

export const environmentParameter = {
  description: i18n.t("common:environmentTypeDesc"),
  existingParameter: true,
  initialTemplateType: "String",
  isMultiSelect: false,
  isOptional: false,
  keyField: "resourceId",
  parameterKey: BLUEPRINT_ENVIRONMENT_KEY,
  parameterLabel: i18n.t("common:environmentType"),
  parameterType: "String",
  parameterValue: "",
  hideParameter: false,
};

export const getUpdatedParameters = (groups = []) => {
  const parameters = groups
    ? groups
        .map((group) => {
          return (
            group.parameters && group.parameters.map((parameter) => parameter)
          );
        })
        .flat()
    : [];
  const doesApplicationKeyExists =
    parameters &&
    parameters
      .map((param) => param && param.parameterKey)
      .includes(BLUEPRINT_APPLICATION_KEY);
  const doesEnvironmentKeyExists =
    parameters &&
    parameters
      .map((param) => param && param.parameterKey)
      .includes(BLUEPRINT_ENVIRONMENT_KEY);
  const doesApplicationConfigurationsGroupExists =
    groups &&
    groups.findIndex(
      (group) =>
        group &&
        group.label &&
        group.label.toLocaleLowerCase() === "application configurations"
    ) >= 0;
  let allGroups = groups ? [...groups] : [];
  if (
    !doesApplicationConfigurationsGroupExists &&
    (!doesEnvironmentKeyExists || !doesApplicationKeyExists)
  ) {
    allGroups.unshift({
      id: `${new Date().valueOf()}`,
      label: "Application Configurations",
      parameters: [],
    });
  }
  allGroups = allGroups.map((group) => {
    if (!doesApplicationKeyExists || !doesEnvironmentKeyExists) {
      if (group.label.toLocaleLowerCase() === "application configurations") {
        let updatedParam = [...group.parameters];
        if (!doesEnvironmentKeyExists) {
          updatedParam.unshift(environmentParameter);
        }
        if (!doesApplicationKeyExists) {
          updatedParam.unshift(applicationParameter);
        }
        return {
          ...group,
          parameters: updatedParam,
        };
      }
    }
    return group;
  });

  allGroups = allGroups.map((group) => {
    return {
      ...group,
      parameters: group.parameters.map((parameter) => {
        if (
          [BLUEPRINT_APPLICATION_KEY, BLUEPRINT_ENVIRONMENT_KEY].includes(
            parameter.parameterKey
          )
        ) {
          return {
            ...parameter,
            isOptional: false,
            hideParameter: false,
          };
        }
        return parameter;
      }),
    };
  });

  return allGroups;
};

export const getParametersRequest = (parameters, isSelfManagedUser) => {
  return parameters.map((p) => {
    let key = p.parameterKey;
    let value = p.parameterValue;
    if (Array.isArray(value)) {
      if (isSelfManagedUser && p.isMultiSelect) {
        value = p.parameterValue;
      } else {
        value = value.join(",");
      }
    }
    if (p.parameterType === "List") {
      if (p.isMultiSelect && Array.isArray(p.parameterValue)) {
        value = p.parameterValue.map((p) => p.label || p);
      } else if (!p.isMultiSelect && Array.isArray(p.parameterValue)) {
        value = p.parameterValue.map((p) => p.label || p).join(",");
      } else if (p.parameterValue === null) {
        value = "";
      } else {
        const val =
          p.parameterValue && (p.parameterValue.value || p.parameterValue);
        value = val && val.toString();
      }
    }

    if (
      p.parameterType.startsWith("List(") ||
      p.parameterType.startsWith("list(") ||
      p.parameterType.startsWith("Tuple(") ||
      p.parameterType.startsWith("tuple(") ||
      p.parameterType.startsWith("Set(") ||
      p.parameterType.startsWith("set(")
    ) {
      value = converLabelValueToTerraformFormat(
        p.parameterType,
        p.parameterValue,
        p.isMultiSelect
      );
    }

    return {
      ParameterKey: key,
      ParameterValue: value,
    };
  });
};

const getParam = (param, parameters) => {
  return (
    parameters &&
    parameters.length > 0 &&
    parameters.find((p) => param.parameterKey === ((p && p.parameterKey) || p))
  );
};

export const getParameterValue = (p, isTask) => {
  let value = p.parameterValue;
  if (Array.isArray(value)) {
    if (p.isMultiSelect && !isTask) {
      value = p.parameterValue;
    } else {
      value = value.join(",");
    }
  }
  if (p.parameterType === "List") {
    if (p.isMultiSelect && Array.isArray(p.parameterValue)) {
      value = p.parameterValue.map((p) => p.label || p);
    } else if (!p.isMultiSelect && Array.isArray(p.parameterValue)) {
      value = p.parameterValue.map((p) => p.label || p).join(",");
    } else {
      const val = p.parameterValue.value
        ? p.parameterValue.value
        : p.parameterValue;
      value = val.toString();
    }
  }
  return value;
};

export const getUpdatedManifest = (manifest, parameters, isFilter = true) => {
  const parameterGroups =
    manifest &&
    manifest.parameters &&
    manifest.parameters.parameterGroups &&
    manifest.parameters.parameterGroups.length > 0 &&
    manifest.parameters.parameterGroups.map((group) => {
      return {
        ...group,
        parameters:
          group && group.parameters && group.parameters.length > 0 && isFilter
            ? group.parameters.filter((param) => {
                return getParam(param, parameters);
              })
            : group.parameters.map((param) => {
                const values = getParam(param, parameters);
                const updatedValues = {
                  ...values,
                  parameterValue: getParameterValue(values),
                  parameterType:
                    (values.parameterType && values.parameterType.value) ||
                    values.parameterType,
                };
                if (values.defaultValue) {
                  updatedValues.defaultValue =
                    (values.defaultValue && values.defaultValue.value) ||
                    values.defaultValue;
                }
                return updatedValues;
              }),
      };
    });
  return (
    isArrayWithLength(parameterGroups) &&
    parameterGroups.filter((group) => isArrayWithLength(group.parameters))
  );
};

export const preProcessParametersWithLaunchConfig = (
  blueprintParameters,
  launchParameters,
  updateParam
) => {
  return (
    blueprintParameters &&
    Array.isArray(blueprintParameters) &&
    blueprintParameters.map((bpParameter) => {
      const foundLaunchParameter =
        launchParameters &&
        Array.isArray(launchParameters) &&
        launchParameters.find(
          (launchParameter) =>
            launchParameter.parameterKey === bpParameter.parameterKey
        );
      let item = {
        ...bpParameter,
      };
      if (foundLaunchParameter) {
        if (foundLaunchParameter.updateRequired) {
          item = {
            ...item,
            updateRequired: true,
            differences: foundLaunchParameter.differences,
          };
        }
        item = {
          ...item,
          ...foundLaunchParameter,
        };
      }
      if (updateParam) {
        item = {
          ...item,
          parameterValue: item.parameterValue || item.defaultValue,
        };
      }
      return item;
    })
  );
};

const filterParams = (parameters) => {
  return (
    parameters &&
    Array.isArray(parameters) &&
    parameters.filter(
      (param) => param.parameterKey !== PARAMETER_KEY_APPLICATION_ID
    )
  );
};

export const filterParametersForLaunchConfig = (groups, parameters) => {
  return {
    parameters: filterParams(parameters),
    groups: groups.map((group) => {
      const item = {
        ...group,
        parameters: filterParams(group.parameters),
      };
      return item;
    }),
  };
};

export const parameterIsVisible = (parameters, currentParameter) => {
  let isVisible = true;
  if (currentParameter.dependentParameter) {
    const dependentParameter = parameters.find(
      (p) => p.parameterKey === currentParameter.dependentParameter.key
    );
    if (dependentParameter) {
      const parameterValue =
        dependentParameter.parameterValue &&
        (dependentParameter.parameterValue.value ||
          dependentParameter.parameterValue);
      isVisible = currentParameter.dependentParameter.value === parameterValue;
    }
  }
  return isVisible;
};

export const validateParameterInputValues = (
  param,
  value,
  isResourcePicker,
  hideResourcePickers,
  validateAll
) => {
  let isValid = true;
  let errorMessage = "";
  const {
    minValue,
    maxValue,
    parameterType,
    minRecordsToBeSelected,
    parameterValue,
  } = param;
  if (parameterValue || validateAll) {
    if (parameterType === "Number") {
      if (Number(value) < Number(minValue)) {
        isValid = false;
        errorMessage = i18n.t("blueprints:minValueError", { minValue });
      }
      if (Number(value) > Number(maxValue)) {
        isValid = false;
        errorMessage = i18n.t("blueprints:maxValueError", { maxValue });
      }
    }
    if (isResourcePicker && !hideResourcePickers) {
      let resources = value;
      if (typeof resources === "string" || resources instanceof String) {
        resources = resources.split(",");
      }
      const hasError =
        !resources || (resources && resources.length < minRecordsToBeSelected);
      isValid = !hasError;
      if (hasError) {
        errorMessage = i18n.t("blueprints:blueprintMinRecordsError", {
          minRecords: minRecordsToBeSelected,
        });
      }
    }
  }

  return {
    isValid,
    errorMessage,
  };
};

export const getIsResourcePicker = (parameterType = "") => {
  const resourceKeys = Object.keys(ResourceList);
  return (
    resourceKeys &&
    Array.isArray(resourceKeys) &&
    resourceKeys.find((r) => r.toLowerCase() === parameterType.toLowerCase())
  );
};
